/**
 * Mimu — three circular meters in the product intro that fill on scroll.
 *
 * Each ring animates from empty to full while its centred number counts up from
 * zero to the target, staggered left to right. Driven by an IntersectionObserver
 * so it fires once when the block enters the viewport, rather than recomputing on
 * every scroll event.
 *
 * The ring itself is animated in CSS (a stroke-dashoffset transition); this file
 * only flips the class that starts it and drives the number, so the two stay in
 * step without JavaScript touching layout on every frame.
 *
 * With prefers-reduced-motion the end state is applied immediately: full ring,
 * final number, no count-up.
 *
 * v2: duration and stagger are read from the --mimu-meter-duration and
 * --mimu-meter-stagger custom properties rather than hardcoded here. The ring is
 * animated by a CSS transition using those same values, so there is one source of
 * truth and the number can never finish out of step with the stroke.
 */
(function () {
  var ROOT = '[data-mimu-meters]';

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** Keep the target's own precision: 3.5 counts in tenths, 10 in whole units. */
  function format(value, decimals, suffix) {
    return value.toFixed(decimals) + suffix;
  }

  function countUp(el, target, decimals, suffix, delay, duration) {
    var started = null;

    function frame(now) {
      if (started === null) started = now;
      var elapsed = now - started;

      if (elapsed < delay) return requestAnimationFrame(frame);

      var t = Math.min((elapsed - delay) / duration, 1);
      // Matches the ring's easing so the number lands as the stroke closes.
      var eased = 1 - Math.pow(1 - t, 3);
      el.textContent = format(target * eased, decimals, suffix);

      if (t < 1) requestAnimationFrame(frame);
      else el.textContent = format(target, decimals, suffix);
    }

    requestAnimationFrame(frame);
  }

  /** Reads a ms-valued custom property off the element, with a fallback. */
  function timing(root, name, fallback) {
    var raw = window.getComputedStyle(root).getPropertyValue(name);
    var value = parseFloat(raw);
    return isNaN(value) ? fallback : value;
  }

  function run(root) {
    if (root.classList.contains('is-in')) return;

    // Read before the class flips, so a transition value cannot be read mid-change.
    var duration = timing(root, '--mimu-meter-duration', 1700);
    var stagger = timing(root, '--mimu-meter-stagger', 300);

    root.classList.add('is-in');

    var meters = root.querySelectorAll('[data-mimu-meter]');
    var still = reduceMotion();

    Array.prototype.forEach.call(meters, function (meter, index) {
      var valueEl = meter.querySelector('[data-mimu-meter-value]');
      if (!valueEl) return;

      var target = parseFloat(meter.getAttribute('data-value'));
      if (isNaN(target)) return;

      var suffix = meter.getAttribute('data-suffix') || '';
      var raw = meter.getAttribute('data-value') || '';
      var decimals = (raw.split('.')[1] || '').length;

      if (still) {
        valueEl.textContent = format(target, decimals, suffix);
        return;
      }

      countUp(valueEl, target, decimals, suffix, index * stagger, duration);
    });
  }

  function init() {
    var roots = document.querySelectorAll(ROOT);
    if (!roots.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(roots, run);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          run(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );

    Array.prototype.forEach.call(roots, function (root) {
      observer.observe(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
